import React, { useState, useEffect } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Spacer,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useDisclosure,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { FaEnvelope, FaEnvelopeOpen, FaEye } from "react-icons/fa";
import axios from "../axiosConfig";
import ListPageLayout from "./common/ListPageLayout";
import TruncatedText from "./common/TruncatedText";

const NAME_MAX_CHARS = 40;
const EMAIL_MAX_CHARS = 40;
const MESSAGE_MAX_CHARS = 80;

const MessagesList = () => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalBusy, setModalBusy] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const subTextColor = useColorModeValue("gray.600", "gray.400");

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await axios.get("/messages");
        setMessages(
          (response.data ?? []).map((message) => ({
            ...message,
            read: message.status === "read",
          }))
        );
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, []);

  const updateMessageStatus = async (messageId, status) => {
    // Brain accepts PATCH /messages/:id with { status: "read" | "unread" }
    const response = await axios.patch(`/messages/${messageId}`, { status });
    const updated = response.data;

    // Update list state
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId
          ? { ...msg, status: updated.status, read: updated.status === "read" }
          : msg
      )
    );

    // Update selected message if it’s the one open
    setSelectedMessage((prev) =>
      prev && prev.id === messageId
        ? { ...prev, status: updated.status, read: updated.status === "read" }
        : prev
    );

    return updated;
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    onOpen();
    if (!message.read) {
      // Mark as read when opened
      updateMessageStatus(message.id, "read").catch((error) => {
        console.error("Error marking message as read:", error);
      });
    }
  };

  const ReadBadge = ({ read }) => (
    <Badge
      variant="subtle"
      colorScheme={read ? "gray" : "blue"}
      borderRadius="md"
    >
      {read ? "Read" : "Unread"}
    </Badge>
  );

  const renderMessageCard = (message) => (
    <Box
      key={message.id}
      p={4}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      shadow="sm"
      w="100%"
      bg={cardBg}
      mb={4}
      _hover={{ shadow: "md" }}
    >
      <VStack align="start" spacing={3} w="100%">
        <HStack justify="space-between" w="100%">
          <HStack spacing={3} minW={0}>
            <Icon
              as={message.read ? FaEnvelopeOpen : FaEnvelope}
              boxSize={5}
              color={message.read ? "gray.500" : "blue.500"}
              flexShrink={0}
            />
            <Box minW={0}>
              <TruncatedText
                value={message.name}
                maxChars={NAME_MAX_CHARS}
                fontWeight={message.read ? "semibold" : "bold"}
              />
              <Text fontSize="sm" color={subTextColor} isTruncated>
                {message.email}
              </Text>
            </Box>
          </HStack>

          <ReadBadge read={message.read} />

        </HStack>

        <Box w="100%" minW={0}>
          <Text fontSize="sm" color={subTextColor} mb={1}>
            Message
          </Text>
          {/* multi-line clamp on mobile feels better than single-line */}
          <Text noOfLines={3}>{message.content}</Text>

        </Box>

        <HStack w="100%" justify="flex-end">
          <Tooltip label="View message" hasArrow>
            <IconButton
              icon={<FaEye />}
              aria-label="View Message"
              variant="outline"
              colorScheme="cyan"
              size="sm"
              onClick={() => openMessage(message)}
            />
          </Tooltip>
        </HStack>
      </VStack>
    </Box>
  );

  const renderMessageTable = () => (
    <Box
      w="100%"
      maxW="100%"
      minW={0}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      bg={cardBg}
      shadow="sm"
      overflow="hidden"
    >
      <TableContainer w="100%" maxW="100%" minW={0} overflowX="auto">
        <Table
          variant="unstyled"
          size="md"
          tableLayout="fixed"
          w="100%"
          maxW="100%"
          sx={{
            "th, td": { borderBottom: "1px solid", borderColor: "gray.100" },
            "tbody tr:hover": { background: "gray.50" },
          }}
        >
          <colgroup>
            <col style={{ width: "10%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "24%" }} />
            <col style={{ width: "32%" }} />
            <col style={{ width: "12%" }} />
          </colgroup>

          <Thead>
            <Tr
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: cardBg,
              }}
            >
              <Th
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Status
              </Th>
              <Th
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Name
              </Th>
              <Th
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Email
              </Th>
              <Th
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Message
              </Th>
              <Th
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Actions
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {messages.map((message) => (
              <Tr key={message.id}>
                <Td py={3}>
                  <HStack spacing={3}>
                    <Icon
                      as={message.read ? FaEnvelopeOpen : FaEnvelope}
                      boxSize={5}
                      color={message.read ? "gray.500" : "blue.500"}
                      flexShrink={0}
                    />
                    <ReadBadge read={message.read} />
                  </HStack>
                </Td>

                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText
                      value={message.name}
                      maxChars={NAME_MAX_CHARS}
                      fontWeight={message.read ? "semibold" : "bold"}
                    />
                  </Box>
                </Td>

                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText value={message.email} maxChars={EMAIL_MAX_CHARS} />
                  </Box>
                </Td>

                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText
                      value={message.content}
                      maxChars={MESSAGE_MAX_CHARS}
                    />
                  </Box>
                </Td>

                <Td py={3}>
                  <Tooltip label="View message" hasArrow>
                    <IconButton
                      icon={<FaEye />}
                      aria-label="View Message"
                      variant="outline"
                      colorScheme="cyan"
                      size="sm"
                      onClick={() => openMessage(message)}
                    />
                  </Tooltip>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <ListPageLayout
      title="Messages"
      actions={null}
      contentProps={{ maxW: "100%", minW: 0 }}
    >
      {loading ? (
        <Flex justify="center" mt={10}>
          <Spinner size="xl" color="orange.400" />
        </Flex>
      ) : messages.length > 0 ? (
        isMobile ? (
          <Box mt={2}>{messages.map(renderMessageCard)}</Box>
        ) : (
          <Box mt={2}>{renderMessageTable()}</Box>
        )
      ) : (
        <Text mt={10} textAlign="center">
          No messages found.
        </Text>
      )}

      {/* Message Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
                  <ModalHeader>
                    <Text fontWeight="bold">{selectedMessage?.name}</Text>
                    <Text fontSize="sm" color={subTextColor}>
                      {selectedMessage?.email}
                    </Text>
                  </ModalHeader>
                  <ModalCloseButton isDisabled={modalBusy} />
        
                  <ModalBody>
                    <Box pb={2}>
                      <HStack mb={3} justify="space-between">
                        <ReadBadge read={selectedMessage?.read} />
                      </HStack>
        
                      <Text fontSize="md" whiteSpace="pre-wrap">
                        {selectedMessage?.content}
                      </Text>

                      <Text fontSize="xs" fontStyle="italic" color={subTextColor} my={1}>Received: {new Date(selectedMessage?.created_at).toLocaleString()}</Text>
                    </Box>
                  </ModalBody>
        
                  <ModalFooter>
                    <HStack spacing={3} >

                      {selectedMessage?.read && (
                        <Button
                          variant="outline"
                          isLoading={modalBusy}
                          onClick={async () => {
                            if (!selectedMessage?.id) return;
                            setModalBusy(true);
                            try {
                              await updateMessageStatus(selectedMessage.id, "unread");
                            } catch (error) {
                              console.error("Error marking message as unread:", error);
                              alert("Failed to mark message as unread.");
                            } finally {
                              setModalBusy(false);
                            }
                          }}
                        >
                          Mark as unread
                        </Button>
                      )}
        
                      <Button onClick={onClose} isDisabled={modalBusy}>
                        Close
                      </Button>
                    </HStack>
                  </ModalFooter>
                </ModalContent>
      </Modal>
    </ListPageLayout>
  );
};

export default MessagesList;